/**
 * Medications Hook
 *
 * Fetches catalog details linked to the current patient’s medications.
 * Medication search uses useMedicationCatalog instead.
 */

import { useMemo } from "react";
import {
  usePatientMedications,
  patientMedicationKeys,
} from "./usePatientMedications";
import type { ResponseBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";

// ============================================================================
// Types
// ============================================================================

export type Medication = NonNullable<
  ResponseBody<
    paths["/{tenantId}/patients/{patientId}/medications"]["get"]
  >["data"][number]["medication"]
>;

// ============================================================================
// Query Keys
// ============================================================================

export const medicationKeys = patientMedicationKeys;

// ============================================================================
// Hooks
// ============================================================================

/** Catalog metadata for the patient's medications, using the shared patient cache. */
export function useMedications(options: { enabled?: boolean } = {}) {
  const query = usePatientMedications(options);
  const data = useMemo(
    () =>
      query.data?.flatMap((record) =>
        record.medication ? [record.medication] : [],
      ),
    [query.data],
  );
  return { ...query, data };
}

/**
 * Find a medication by name (case-insensitive).
 * Returns the medication object if found, null otherwise.
 */
export function findMedicationByName(
  medications: Medication[],
  name: string,
): Medication | null {
  const lowerName = name.toLowerCase();
  return medications.find((m) => m.name?.toLowerCase() === lowerName) ?? null;
}
